import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  FileJson, 
  Edit, 
  Trash2, 
  Send,
  Bot,
  User,
  Sparkles,
  Code,
  Save,
  X,
  Loader2,
  Copy
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const XMLTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [builderMode, setBuilderMode] = useState('ai');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(null);

  // Form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    voucher_type: '',
    xml_content: '',
    description: ''
  });

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const chatEndRef = useRef(null);

  const voucherTypes = [
    'Sales', 'Purchase', 'Payment', 'Receipt', 'Journal', 
    'Contra', 'Credit Note', 'Debit Note', 'Stock Journal',
    'Ledger', 'Item', 'Currency', 'Group'
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await apiService.getXMLTemplates();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const openBuilder = (template = null) => {
    const newSessionId = `xml_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    setChatMessages([{
      role: 'assistant',
      content: "Hello! I'm your AI assistant for generating Tally XML templates. I can help you create:\n\n• Sales/Purchase vouchers\n• Payment/Receipt vouchers\n• Journal entries\n• And more!\n\nWhat type of voucher do you need? Please also tell me:\n• The ledger names involved\n• Entry type (single or multiple entries)\n• Any specific fields needed"
    }]);
    
    if (template) {
      setSelectedTemplate(template);
      setTemplateForm({
        name: template.name,
        voucher_type: template.voucher_type,
        xml_content: template.xml_content,
        description: template.description || ''
      });
    } else {
      setSelectedTemplate(null);
      setTemplateForm({
        name: '',
        voucher_type: '',
        xml_content: '',
        description: ''
      });
    }
    setIsBuilderOpen(true);
  };

  const closeBuilder = () => {
    setIsBuilderOpen(false);
    setSelectedTemplate(null);
    setChatMessages([]);
    setChatInput('');
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await apiService.sendAIMessage({
        session_id: sessionId,
        message: userMessage,
        context_type: 'xml'
      });

      const aiResponse = response.data;
      setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse.response }]);

      if (aiResponse.xml_content) {
        setTemplateForm(prev => ({ ...prev, xml_content: aiResponse.xml_content }));
        toast.success('XML template generated! Review and save when ready.');
      }
    } catch (error) {
      toast.error('AI service error. Please try again.');
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again or write your XML manually.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSave = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!templateForm.voucher_type) {
      toast.error('Please select a voucher type');
      return;
    }
    if (!templateForm.xml_content.trim()) {
      toast.error('Please enter XML content');
      return;
    }

    try {
      if (selectedTemplate) {
        await apiService.updateXMLTemplate(selectedTemplate.id, templateForm);
        toast.success('Template updated');
      } else {
        await apiService.createXMLTemplate(templateForm);
        toast.success('Template created');
      }
      loadTemplates();
      closeBuilder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(templateForm.xml_content);
    toast.success('XML copied to clipboard');
  };

  const openDeleteDialog = (template) => {
    setDeletingTemplate(template);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;
    
    try {
      await apiService.deleteXMLTemplate(deletingTemplate.id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingTemplate(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Builder View
  if (isBuilderOpen) {
    return (
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="xml-builder">
        {/* Builder Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={closeBuilder}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <h2 className="font-heading font-semibold text-lg">
              {selectedTemplate ? 'Edit XML Template' : 'New XML Template'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <Button
                variant={builderMode === 'ai' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBuilderMode('ai')}
                className={builderMode === 'ai' ? 'bg-teal-700 hover:bg-teal-600' : ''}
                data-testid="xml-ai-mode-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                AI Chat
              </Button>
              <Button
                variant={builderMode === 'manual' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBuilderMode('manual')}
                className={builderMode === 'manual' ? 'bg-teal-700 hover:bg-teal-600' : ''}
                data-testid="xml-manual-mode-btn"
              >
                <Code className="h-4 w-4 mr-2" />
                Manual
              </Button>
            </div>
            <Button
              className="bg-teal-700 hover:bg-teal-600"
              size="sm"
              onClick={handleSave}
              data-testid="save-template-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Template
            </Button>
          </div>
        </div>

        {/* Builder Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - AI Chat or Config */}
          <div className="w-2/5 border-r border-slate-200 flex flex-col bg-white">
            {/* Template Config */}
            <div className="p-4 border-b border-slate-200 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Template Name</Label>
                  <Input
                    placeholder="e.g., Sales Invoice"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    className="h-9"
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Voucher Type</Label>
                  <Select
                    value={templateForm.voucher_type}
                    onValueChange={(value) => setTemplateForm({ ...templateForm, voucher_type: value })}
                  >
                    <SelectTrigger className="h-9" data-testid="voucher-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {voucherTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description (Optional)</Label>
                <Input
                  placeholder="Brief description of this template"
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>

            {builderMode === 'ai' ? (
              /* AI Chat Interface */
              <>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] ${
                          msg.role === 'user' 
                            ? 'chat-bubble-user' 
                            : 'chat-bubble-ai'
                        }`}>
                          <div className="flex items-start gap-2">
                            {msg.role === 'assistant' && (
                              <Bot className="h-4 w-4 mt-0.5 text-teal-600 flex-shrink-0" />
                            )}
                            <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                            {msg.role === 'user' && (
                              <User className="h-4 w-4 mt-0.5 text-slate-500 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="chat-bubble-ai">
                          <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-teal-600" />
                            <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
                            <span className="text-sm text-slate-500">Generating XML...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-4 border-t border-slate-200">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Describe the voucher you need... e.g., 'Sales invoice for product sale'"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                      disabled={chatLoading}
                      className="flex-1"
                      data-testid="xml-chat-input"
                    />
                    <Button
                      className="bg-teal-700 hover:bg-teal-600"
                      onClick={sendChatMessage}
                      disabled={chatLoading || !chatInput.trim()}
                      data-testid="xml-send-chat-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* Manual XML Info */
              <div className="flex-1 p-4">
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
                  <h4 className="font-medium text-slate-900 mb-2">Tally XML Structure</h4>
                  <p className="mb-3">Your XML should follow the standard Tally import format with these key elements:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• <code className="bg-slate-200 px-1 rounded">ENVELOPE</code> - Root element</li>
                    <li>• <code className="bg-slate-200 px-1 rounded">HEADER</code> - Contains request type</li>
                    <li>• <code className="bg-slate-200 px-1 rounded">BODY</code> - Contains data</li>
                    <li>• <code className="bg-slate-200 px-1 rounded">IMPORTDATA</code> - Import section</li>
                    <li>• <code className="bg-slate-200 px-1 rounded">REQUESTDATA</code> - Request section</li>
                    <li>• <code className="bg-slate-200 px-1 rounded">TALLYMESSAGE</code> - Message container</li>
                    <li>• <code className="bg-slate-200 px-1 rounded">VOUCHER</code> - Voucher data</li>
                  </ul>
                  <p className="mt-3 text-xs text-slate-500">
                    Use placeholders like {"{{FIELD_NAME}}"} for dynamic values that will be mapped from datasets.
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    For invoices with multiple line items, wrap the repeating part in{' '}
                    <code className="bg-slate-200 px-1 rounded">{"{{#LINEITEMS}}"}</code> …{' '}
                    <code className="bg-slate-200 px-1 rounded">{"{{/LINEITEMS}}"}</code>. Each
                    dataset row in a group repeats that block once, producing a single voucher.
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    Add a <code className="bg-slate-200 px-1 rounded">{"<GUID>{{GUID}}</GUID>"}</code> tag —
                    it is auto-filled with a unique ID per post and saved as a reference so the
                    voucher can be altered or re-posted later.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - XML Editor */}
          <div className="flex-1 flex flex-col bg-slate-50">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-white">
              <Label className="text-xs font-medium">XML Content</Label>
              <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
            <div className="flex-1 p-4">
              <Textarea
                placeholder={`<ENVELOPE>
  <HEADER>
    <TALLYREQUEST>Import Data</TALLYREQUEST>
  </HEADER>
  <BODY>
    <IMPORTDATA>
      <REQUESTDESC>
        <REPORTNAME>Vouchers</REPORTNAME>
        <STATICVARIABLES>
          <SVCURRENTCOMPANY>{{COMPANY_NAME}}</SVCURRENTCOMPANY>
        </STATICVARIABLES>
      </REQUESTDESC>
      <REQUESTDATA>
        <TALLYMESSAGE>
          <VOUCHER VCHTYPE="{{VOUCHER_TYPE}}" ACTION="Create">
            <GUID>{{GUID}}</GUID>
            <DATE>{{INVOICE_DATE}}</DATE>
            <PARTYLEDGERNAME>{{PARTY_NAME}}</PARTYLEDGERNAME>
            <!-- Repeats once per dataset row in the group -->
            {{#LINEITEMS}}
            <ALLINVENTORYENTRIES.LIST>
              <STOCKITEMNAME>{{ITEM_NAME}}</STOCKITEMNAME>
              <ACTUALQTY>{{QTY}}</ACTUALQTY>
              <RATE>{{RATE}}</RATE>
              <AMOUNT>{{AMOUNT}}</AMOUNT>
            </ALLINVENTORYENTRIES.LIST>
            {{/LINEITEMS}}
          </VOUCHER>
        </TALLYMESSAGE>
      </REQUESTDATA>
    </IMPORTDATA>
  </BODY>
</ENVELOPE>`}
                value={templateForm.xml_content}
                onChange={(e) => setTemplateForm({ ...templateForm, xml_content: e.target.value })}
                className="h-full font-mono text-sm resize-none bg-slate-900 text-slate-100 border-none"
                data-testid="xml-content-textarea"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="page-container" data-testid="xml-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">XML Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage Tally XML voucher templates</p>
        </div>
        <Button 
          className="bg-teal-700 hover:bg-teal-600"
          onClick={() => openBuilder()}
          data-testid="create-template-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead className="table-cell">Name</TableHead>
                <TableHead className="table-cell">Voucher Type</TableHead>
                <TableHead className="table-cell">Tags</TableHead>
                <TableHead className="table-cell">Version</TableHead>
                <TableHead className="table-cell">Last Updated</TableHead>
                <TableHead className="table-cell text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    <FileJson className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No XML templates created yet</p>
                    <p className="text-sm">Click "Create Template" to build your first Tally XML</p>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template.id} className="table-row" data-testid={`template-row-${template.id}`}>
                    <TableCell className="table-cell">
                      <div>
                        <p className="font-medium">{template.name}</p>
                        {template.description && (
                          <p className="text-xs text-slate-500 truncate max-w-xs">{template.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="table-cell">
                      <Badge className="badge-neutral">{template.voucher_type}</Badge>
                    </TableCell>
                    <TableCell className="table-cell">
                      <span className="text-sm text-slate-500">
                        {template.tags?.length || 0} tags
                      </span>
                    </TableCell>
                    <TableCell className="table-cell">v{template.version}</TableCell>
                    <TableCell className="table-cell text-sm text-slate-500">
                      {format(parseISO(template.updated_at), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openBuilder(template)}
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openDeleteDialog(template)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingTemplate?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-template-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default XMLTemplatesPage;
